import { useForm } from 'react-hook-form'
import { useDisclosure } from '@chakra-ui/react'
import { erc20Abi, formatUnits, parseUnits } from 'viem'
import { useReadContract, useWriteContract } from 'wagmi'

import { ToastType } from '~/components/dashboard/Toast'
import { DEFAULT_DECIMALS, secondsInADay, secondsInAYear } from '~/constants'
import { addresses } from '~/constants/addresses'
import { delay } from '~/helpers'
import RewardPool from '~/lib/abis/RewardPool'
import * as Analytics from '~/lib/analytics'
import { useDistributionToken } from './useDistributionToken'
import { useHydratedAccount } from './useHydratedAccount'
import useSupportedNetworkId from './useSupportedNetworkId'
import { useToastMessage } from './useToastMessage'

const useDeeprStaking = () => {
  const chainId = useSupportedNetworkId()

  const { isOpen: isStakingModalOpen, onOpen: onOpenStakingModal, onClose: onCloseStakingModal } = useDisclosure()

  const { address } = useHydratedAccount()

  const { distributionToken } = useDistributionToken()

  const { showToast } = useToastMessage()

  const form = useForm({ mode: 'onChange' })

  const depositAmount = form.watch('deposit')

  const depositAmountBN = parseUnits(
    (depositAmount ?? 0).toString(),
    distributionToken?.underlyingDecimals ?? DEFAULT_DECIMALS
  )

  const stakingAddress = addresses[chainId].staking

  const { data: stakingData = [], refetch: refetchStakingData } = useReadContract({
    address: stakingAddress,
    chainId,
    abi: RewardPool,
    functionName: 'userInfo',
    args: [address],
  })

  const { data: pendingRewards, refetch: refetchPendingRewards } = useReadContract({
    address: stakingAddress,
    chainId,
    abi: RewardPool,
    functionName: 'pendingRewards',
    args: [address],
  })

  const { data: hasActiveWithdrawRequest, refetch: refetchHasActiveWithdrawRequest } = useReadContract({
    address: stakingAddress,
    chainId,
    abi: RewardPool,
    functionName: 'hasActiveWithdrawRequest',
    args: [address],
  })

  const { data: userLockEndTimestamp, refetch: refetchUserLockEndTimestamp } = useReadContract({
    address: stakingAddress,
    chainId,
    abi: RewardPool,
    functionName: 'userLockEndTimestamp',
    args: [address],
  })

  const stakingDataArray = stakingData as Array<bigint | boolean>

  const userStakingInfo = {
    stakedAmount: stakingDataArray[0] as bigint,
    lockedRequestedAmount: stakingDataArray[1] as bigint,
    rewardDebt: stakingDataArray[2] as bigint,
    isRequestStarted: stakingDataArray[3] as boolean,
    requestTimestamp: stakingDataArray[4] as bigint,
    pendingRewards: pendingRewards as bigint,
    hasActiveWithdrawRequest: hasActiveWithdrawRequest as boolean,
    userLockEndTimestamp: userLockEndTimestamp as number,
  }

  const { status: writeApproveStatus, writeContractAsync: writeApprove } = useWriteContract()
  const isLoadingApprove = writeApproveStatus === 'pending'

  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: distributionToken.underlyingTokenAddress,
    abi: erc20Abi,
    functionName: 'allowance',
    args: [address ?? '0x', stakingAddress ?? '0x'],
  })

  const approve = async () => {
    if (!distributionToken.underlyingTokenAddress) {
      return
    }

    try {
      await writeApprove({
        abi: erc20Abi,
        address: distributionToken.underlyingTokenAddress,
        functionName: 'approve',
        args: [stakingAddress ?? '0x', depositAmountBN],
      })

      await delay(1500)
      refetchAllowance()

      showToast({ variant: ToastType.SUCCESS, title: 'Approved successfully' })
    } catch (e) {
      showToast({ variant: ToastType.FAILURE, title: 'Approve failed' })

      Analytics.trackException(e)
    }
  }

  const hasAllowance = !!allowance && allowance >= depositAmountBN

  const { status: writeDepositStatus, writeContractAsync: writeDeposit } = useWriteContract()
  const isLoadingDeposit = writeDepositStatus === 'pending'

  const deposit = async () => {
    if (!stakingAddress) {
      return
    }

    try {
      await writeDeposit({
        address: stakingAddress,
        abi: RewardPool,
        functionName: 'deposit',
        args: [depositAmountBN],
      })

      await refetchAll()

      showToast({ variant: ToastType.SUCCESS, title: `Deposited successfully` })

      onCloseStakingModal()

      form.setValue('deposit', 0)
    } catch (e) {
      showToast({ variant: ToastType.FAILURE, title: 'Deposit failed' })

      Analytics.trackException(e)
    }
  }

  const { status: writeHarvestStatus, writeContractAsync: writeHarvest } = useWriteContract()
  const isLoadingHarvest = writeHarvestStatus === 'pending'

  const refetchAll = async () => {
    await delay(1500)
    refetchStakingData()
    refetchPendingRewards()
    refetchHasActiveWithdrawRequest()
    refetchUserLockEndTimestamp()
  }

  const harvest = async () => {
    if (!stakingAddress) {
      return
    }

    try {
      await writeHarvest({
        address: stakingAddress,
        abi: RewardPool,
        functionName: 'harvest',
        args: [],
      })

      await refetchAll()

      showToast({
        variant: ToastType.SUCCESS,
        title: 'Harvested successfully',
      })
    } catch (e) {
      showToast({ variant: ToastType.FAILURE, title: 'Harvest failed' })

      Analytics.trackException(e)
    }
  }

  const { status: writeWithdrawRequestStatus, writeContractAsync: writeWithdrawRequest } = useWriteContract()
  const isLoadingWithdrawRequest = writeWithdrawRequestStatus === 'pending'

  const requestWithdraw = async () => {
    if (!stakingAddress) {
      return
    }

    try {
      await writeWithdrawRequest({
        address: stakingAddress,
        abi: RewardPool,
        functionName: 'withdrawRequestAndHarvest',
        args: [],
      })

      await refetchAll()

      onCloseStakingModal()
      showToast({ variant: ToastType.SUCCESS, title: 'Withdrawal requested' })
    } catch (e) {
      showToast({ variant: ToastType.FAILURE, title: 'Withdrawal request failed' })

      Analytics.trackException(e)
    }
  }

  const { status: writeWithdrawStatus, writeContractAsync: writeWithdraw } = useWriteContract()
  const isLoadingWithdraw = writeWithdrawStatus === 'pending'

  const withdraw = async () => {
    if (!stakingAddress) {
      return
    }

    try {
      if (Date.now() < Number(userLockEndTimestamp)) {
        showToast({ variant: ToastType.FAILURE, title: 'You cannot withdraw your funds yet' })

        return
      }

      await writeWithdraw({
        address: stakingAddress,
        abi: RewardPool,
        functionName: 'withdrawLockedTokens',
        args: [],
      })

      await refetchAll()

      showToast({ variant: ToastType.SUCCESS, title: 'Withdrawal successful' })
    } catch (e) {
      showToast({ variant: ToastType.FAILURE, title: 'Withdrawal failed' })

      Analytics.trackException(e)
    }
  }

  const { data: rewardsPerSecBN } = useReadContract({
    address: stakingAddress,
    chainId,
    abi: RewardPool,
    functionName: 'rewardsPerSec',
    args: [],
  })

  const { data: stakedSupplyBN } = useReadContract({
    address: stakingAddress,
    chainId,
    abi: RewardPool,
    functionName: 'stakedSupply',
    args: [],
  })

  const stakedSupply = parseFloat(
    formatUnits((stakedSupplyBN as bigint) ?? BigInt(0), distributionToken.underlyingDecimals ?? DEFAULT_DECIMALS)
  )

  const rewardsPerSec = parseFloat(
    formatUnits((rewardsPerSecBN as bigint) ?? BigInt(0), distributionToken.underlyingDecimals ?? DEFAULT_DECIMALS)
  )

  const potentialPoolShare = (distributionToken.balance ?? 0) / (stakedSupply + (distributionToken.balance ?? 0))

  const potentialDailyRewards = rewardsPerSec * secondsInADay * (potentialPoolShare || 0)

  const APR = rewardsPerSec * secondsInAYear * (1 / (stakedSupply || 1))

  const marketStakingInfo = {
    potentialDailyRewards,
    APR,
    stakedSupply,
    rewardsPerSec,
  }

  return {
    approve,
    deposit,
    harvest,
    requestWithdraw,
    withdraw,

    userStakingInfo,
    marketStakingInfo,

    hasAllowance,
    isLoadingApprove,
    isLoadingDeposit,
    isLoadingHarvest,
    isLoadingWithdraw,
    isLoadingWithdrawRequest,

    form,
    isStakingModalOpen,
    onOpenStakingModal,
    onCloseStakingModal,
  }
}

export default useDeeprStaking
